import type { AccountInfo } from '@azure/msal-browser';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AuthContext } from '@/auth/AuthContext';
import {
  authFailureEventName,
  type AuthFailureEventDetail,
} from '@/auth/authFailureEvents';
import { authService } from '@/auth/services/AuthService';
import { toAuthErrorKey } from '@/auth/services/authError';
import { runLoginRedirectOnce } from '@/auth/utils/loginRedirectGuard';
import { authConfig } from '@/config/authConfig';
import type {
  AuthAccount,
  AuthErrorKey,
  AuthStatus,
  AuthUser,
} from '@/types/auth';

const parentLogoutEventName = 'entity-portal:auth-logout';

interface AuthProviderProps {
  children: ReactNode;
}

const selectedAccountSessionKey = 'entity_portal_auth_selected_account_id';

const getStoredSelectedAccountId = (): string | null => {
  try {
    return window.sessionStorage.getItem(selectedAccountSessionKey);
  } catch {
    return null;
  }
};

const setStoredSelectedAccountId = (accountId: string): void => {
  try {
    window.sessionStorage.setItem(selectedAccountSessionKey, accountId);
  } catch {
    // Ignore storage failures.
  }
};

const clearStoredSelectedAccountId = (): void => {
  try {
    window.sessionStorage.removeItem(selectedAccountSessionKey);
  } catch {
    // Ignore storage failures.
  }
};

const buildUserFromAccount = (account: AccountInfo): AuthUser => {
  const idTokenClaims = account.idTokenClaims as
    | {
        oid?: string;
        preferred_username?: string;
        email?: string;
        name?: string;
        roles?: string[];
      }
    | undefined;

  const email =
    idTokenClaims?.preferred_username ??
    idTokenClaims?.email ??
    account.username ??
    '';

  return {
    id: idTokenClaims?.oid ?? account.localAccountId,
    displayName: idTokenClaims?.name ?? account.name ?? email,
    email,
    username: account.username,
    roles: idTokenClaims?.roles ?? [],
  };
};

const buildAuthAccountFromAccount = (account: AccountInfo): AuthAccount => {
  const idTokenClaims = account.idTokenClaims as
    | {
        preferred_username?: string;
        email?: string;
        name?: string;
      }
    | undefined;

  const email =
    idTokenClaims?.preferred_username ??
    idTokenClaims?.email ??
    account.username ??
    '';

  const accountId = account.homeAccountId || account.localAccountId;

  return {
    id: accountId,
    displayName: idTokenClaims?.name ?? account.name ?? email,
    email,
    username: account.username,
  };
};

const isIgnorableRedirectError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const errorCode =
    'errorCode' in error
      ? String((error as { errorCode?: string }).errorCode ?? '').toLowerCase()
      : '';
  const message =
    'message' in error
      ? String((error as { message?: string }).message ?? '').toLowerCase()
      : '';

  return (
    errorCode.includes('hash_empty_error') ||
    message.includes('hash_empty_error') ||
    errorCode.includes('state_mismatch')
  );
};

const graphProfilePhotoEndpoint =
  'https://graph.microsoft.com/v1.0/me/photo/$value';

const revokePhotoUrl = (photoUrl: string | undefined): void => {
  if (!photoUrl || !photoUrl.startsWith('blob:')) {
    return;
  }

  try {
    URL.revokeObjectURL(photoUrl);
  } catch {
    // Ignore blob URL cleanup failures.
  }
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [errorKey, setErrorKey] = useState<AuthErrorKey | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<AuthAccount[]>([]);
  const [requiresAccountSelection, setRequiresAccountSelection] =
    useState(false);
  const [isAuthActionPending, setIsAuthActionPending] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const profilePhotoUrlRef = useRef<string | undefined>(undefined);

  const clearProfilePhoto = useCallback(() => {
    revokePhotoUrl(profilePhotoUrlRef.current);
    profilePhotoUrlRef.current = undefined;
    setUser((currentUser) => {
      if (!currentUser?.photoUrl) {
        return currentUser;
      }

      return {
        ...currentUser,
        photoUrl: undefined,
      };
    });
  }, []);

  const clearError = useCallback(() => {
    setErrorKey(null);
  }, []);

  const restoreSession = useCallback(async () => {
    if (!authConfig.isConfigured) {
      setStatus('unauthenticated');
      setErrorKey('authConfigurationError');
      return;
    }

    setStatus('loading');

    try {
      await authService.initialize();
      try {
        await authService.handleRedirectResponse();
      } catch (error) {
        if (!isIgnorableRedirectError(error)) {
          throw error;
        }
      }

      const activeAccount = authService.getActiveAccount();
      const allAccounts = authService.getAllAccounts();

      let account = activeAccount;

      if (!account && allAccounts.length === 1) {
        account = allAccounts[0];
        authService.setActiveAccount(account);
      }

      if (!account) {
        const selectedAccountId = getStoredSelectedAccountId();

        if (selectedAccountId) {
          account = authService.getAccountById(selectedAccountId);

          if (account) {
            authService.setActiveAccount(account);
          }
        }
      }

      if (!account) {
        clearProfilePhoto();
        if (allAccounts.length > 1) {
          setAvailableAccounts(allAccounts.map(buildAuthAccountFromAccount));
          setRequiresAccountSelection(true);
        } else {
          setAvailableAccounts([]);
          setRequiresAccountSelection(false);
        }

        setUser(null);
        setStatus('unauthenticated');
        setErrorKey(null);
        return;
      }

      setIsProfileLoading(true);

      await authService.acquireTokenSilently(account);

      const profile = buildUserFromAccount(account);
      setStoredSelectedAccountId(
        account.homeAccountId || account.localAccountId,
      );
      setAvailableAccounts([]);
      setRequiresAccountSelection(false);
      setUser(profile);
      setStatus('authenticated');
      setErrorKey(null);
    } catch (error) {
      clearProfilePhoto();
      setAvailableAccounts([]);
      setRequiresAccountSelection(false);
      setUser(null);
      setStatus('unauthenticated');
      setErrorKey(toAuthErrorKey(error));
    } finally {
      setIsProfileLoading(false);
    }
  }, [clearProfilePhoto]);

  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  useEffect(() => {
    const handleAuthFailure = (event: Event) => {
      const detail = (event as CustomEvent<AuthFailureEventDetail>).detail;

      if (!detail) {
        return;
      }

      if (detail.reason === 'sessionExpired') {
        clearStoredSelectedAccountId();
        clearProfilePhoto();
        setAvailableAccounts([]);
        setRequiresAccountSelection(false);
        setIsAuthActionPending(false);
        setIsProfileLoading(false);
        setUser(null);
        setStatus('unauthenticated');
        setErrorKey('authSessionExpired');
        return;
      }

      setErrorKey('authUnauthorized');
    };

    window.addEventListener(authFailureEventName, handleAuthFailure);

    return () => {
      window.removeEventListener(authFailureEventName, handleAuthFailure);
    };
  }, [clearProfilePhoto]);

  const login = useCallback(
    async (redirectStartPage?: string) => {
      if (isAuthActionPending) {
        setErrorKey('authInteractionInProgress');
        return;
      }

      setIsAuthActionPending(true);
      setErrorKey(null);
      setRequiresAccountSelection(false);
      setAvailableAccounts([]);

      try {
        clearProfilePhoto();
        const redirectStarted = await runLoginRedirectOnce(async () => {
          await authService.initialize();
          await authService.login(redirectStartPage);
        });

        if (!redirectStarted) {
          setErrorKey('authInteractionInProgress');
          setIsAuthActionPending(false);
        }
      } catch (error) {
        setErrorKey(toAuthErrorKey(error));
        setStatus('unauthenticated');
        setIsAuthActionPending(false);
      }
    },
    [clearProfilePhoto, isAuthActionPending],
  );

  const selectAccount = useCallback(
    async (accountId: string) => {
      setIsProfileLoading(true);
      setErrorKey(null);

      try {
        await authService.initialize();

        const selectedAccount = authService.getAccountById(accountId);

        if (!selectedAccount) {
          setErrorKey('authFailed');
          setStatus('unauthenticated');
          return;
        }

        authService.setActiveAccount(selectedAccount);
        await authService.acquireTokenSilently(selectedAccount);

        setStoredSelectedAccountId(accountId);
        clearProfilePhoto();
        setUser(buildUserFromAccount(selectedAccount));
        setStatus('authenticated');
        setRequiresAccountSelection(false);
        setAvailableAccounts([]);
        setErrorKey(null);
      } catch (error) {
        setErrorKey(toAuthErrorKey(error));
        setStatus('unauthenticated');
      } finally {
        setIsProfileLoading(false);
      }
    },
    [clearProfilePhoto],
  );

  const logout = useCallback(async () => {
    setIsAuthActionPending(true);
    setErrorKey(null);

    window.dispatchEvent(new Event(parentLogoutEventName));
    clearStoredSelectedAccountId();
    clearProfilePhoto();
    setAvailableAccounts([]);
    setRequiresAccountSelection(false);
    setIsProfileLoading(false);
    setUser(null);
    setStatus('unauthenticated');

    try {
      await authService.logout();
    } catch (error) {
      setIsAuthActionPending(false);
      setErrorKey(toAuthErrorKey(error));
    }
  }, [clearProfilePhoto]);

  useEffect(() => {
    if (status !== 'authenticated' || !user?.id) {
      return;
    }

    let isCancelled = false;

    const enrichProfileWithPhoto = async () => {
      try {
        const activeAccount = authService.getActiveAccount();
        const account =
          activeAccount ??
          authService
            .getAllAccounts()
            .find(
              (item) =>
                item.homeAccountId === user.id ||
                item.localAccountId === user.id ||
                item.username.toLowerCase() === user.username.toLowerCase(),
            );

        if (!account) {
          return;
        }

        const tokenResponse = await authService.acquireToken(account, [
          'User.Read',
        ]);
        const accessToken = tokenResponse.accessToken;

        if (!accessToken) {
          return;
        }

        const response = await fetch(graphProfilePhotoEndpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const photoBlob = await response.blob();

        if (!photoBlob.size || isCancelled) {
          return;
        }

        const nextPhotoUrl = URL.createObjectURL(photoBlob);

        setUser((currentUser) => {
          if (!currentUser || currentUser.id !== user.id || isCancelled) {
            revokePhotoUrl(nextPhotoUrl);
            return currentUser;
          }

          revokePhotoUrl(profilePhotoUrlRef.current);
          profilePhotoUrlRef.current = nextPhotoUrl;

          return {
            ...currentUser,
            photoUrl: nextPhotoUrl,
          };
        });
      } catch {
        // Profile photo is optional and must not block or fail login/session restore.
      }
    };

    void enrichProfileWithPhoto();

    return () => {
      isCancelled = true;
    };
  }, [status, user?.id, user?.username]);

  useEffect(() => {
    return () => {
      revokePhotoUrl(profilePhotoUrlRef.current);
      profilePhotoUrlRef.current = undefined;
    };
  }, []);

  const value = useMemo(
    () => ({
      status,
      user,
      errorKey,
      availableAccounts,
      requiresAccountSelection,
      isAuthActionPending,
      isProfileLoading,
      isAuthenticated: status === 'authenticated',
      login,
      selectAccount,
      logout,
      clearError,
    }),
    [
      availableAccounts,
      clearError,
      errorKey,
      isAuthActionPending,
      isProfileLoading,
      login,
      logout,
      requiresAccountSelection,
      selectAccount,
      status,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
